import {usePermissions} from '@shared/hooks/usePermissions';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useAuthStore } from '@shared/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import {
  ProductOverlayStockApiAdapter,
  type ProductOverlayLocationStock,
  type ProductOverlayStockSummary,
} from '../adapters/ProductOverlayStockApiAdapter';

export interface UseProductOverlayStockResult {
  summary: ProductOverlayStockSummary | null;
  locations: ProductOverlayLocationStock[];
  isLoading: boolean;
  error: string | null;
  showLocations: boolean;
  canViewStock:boolean;
}

export function useProductOverlayStock(
  productId: number | undefined,
  enabled: boolean,
): UseProductOverlayStockResult {
  const {isBlockModel,hasPermission,authorizationScope}=usePermissions();
  const canReadProduct=!isBlockModel || hasPermission('access.products.view');
  const canAccess = useAuthStore((s) => s.canAccess);
  const showLocations = canAccess(['tenant_3pl'], []) && (!isBlockModel || hasPermission('access.inventory.view'));

  const adapter = new ProductOverlayStockApiAdapter(createHttpClient(apiClientWH));
  const queryEnabled = Boolean(enabled && productId);

  const summaryQuery = useQuery({
    queryKey: ['product', productId, 'overlay-stock-summary',authorizationScope],
    enabled: queryEnabled && canReadProduct,
    queryFn: () => adapter.getStockSummary(productId!),
    staleTime: 60 * 1000,
  });

  const locationsQuery = useQuery({
    queryKey: ['locations-by-product', productId, 'overlay',authorizationScope],
    enabled: queryEnabled && showLocations,
    queryFn: () => adapter.getLocationsWithStock(productId!),
    staleTime: 60 * 1000,
  });

  const isLoading =
    queryEnabled &&
    (summaryQuery.isLoading || (showLocations && locationsQuery.isLoading));

  const errorMessage =
    (summaryQuery.error as Error | null)?.message ||
    (locationsQuery.error as Error | null)?.message ||
    null;

  return {
    summary: canReadProduct ? summaryQuery.data ?? null:null,
    locations: showLocations ? locationsQuery.data ?? []:[],
    isLoading,
    error: errorMessage,
    showLocations,
    canViewStock:canReadProduct || showLocations,
  };
}
